<template>
  <div>
    <h1 class="page-title">Inscricoes</h1>

    <SemEventoBanner v-if="organizadorSemEventos" :nome="authStore.adminName" />

    <div v-if="!organizadorSemEventos" class="filters card">
      <input class="input search" type="text" v-model="q" @input="debouncedFetch" placeholder="Buscar por nome, CPF ou email..." />
      <select class="select" v-model="eventoSlug" @change="fetch()">
        <option value="">Todos eventos</option>
        <option v-for="e in eventosStore.eventos" :key="e.id" :value="e.slug">{{ e.nome }}</option>
      </select>
      <select class="select" v-model="status" @change="fetch()">
        <option value="">Todos status</option>
        <option value="PENDENTE">Pendente</option>
        <option value="PARCIAL">Parcial</option>
        <option value="QUITADA">Quitada</option>
        <option value="ATRASADA">Atrasada</option>
      </select>
      <!-- So aparece com um evento escolhido: lista de varios eventos
           misturados nao serve de checklist de portaria para ninguem. -->
      <button
        v-if="authStore.canExportarLista"
        class="btn-secondary btn-exportar"
        :disabled="!eventoSlug || exportando"
        :title="!eventoSlug ? 'Escolha um evento para exportar' : ''"
        @click="exportarListaPdf"
      >{{ exportando ? 'Gerando…' : '📄 Exportar PDF' }}</button>
    </div>

    <SkeletonLoader v-if="store.loading" variant="row" :count="8" />

    <div v-else-if="store.items.length" class="table-card card">
      <div class="table-wrap"><table class="table">
        <thead>
          <tr>
            <th>Participante</th>
            <th>Evento</th>
            <th>Plano</th>
            <th>Status</th>
            <th>Data</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="i in store.items" :key="i.id" @click="openDetail(i)" class="clickable">
            <td>
              <strong>{{ i.participanteNome }}</strong>
              <span v-if="i.grupoInscricao && i.grupoInscricao.tamanho > 1" class="grupo-badge" :title="`Grupo de ${i.grupoInscricao.tamanho}`">
                👥 {{ i.posicaoNoGrupo }}/{{ i.grupoInscricao.tamanho }}
              </span>
              <br>
              <small>{{ i.comprador?.email }}</small>
            </td>
            <td>{{ i.evento?.nome }}</td>
            <td>
              <span v-if="i.plano">{{ i.plano.nome }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td><StatusChip :status="i.statusPagamento" /></td>
            <td>{{ formatDate(i.createdAt) }}</td>
            <td>→</td>
          </tr>
        </tbody>
      </table></div>

      <div v-if="store.meta.pages > 1" class="pagination">
        <button class="btn-secondary" :disabled="store.meta.page <= 1" @click="changePage(store.meta.page - 1)">‹ Anterior</button>
        <span class="page-info">Pagina {{ store.meta.page }} de {{ store.meta.pages }}</span>
        <button class="btn-secondary" :disabled="store.meta.page >= store.meta.pages" @click="changePage(store.meta.page + 1)">Proxima ›</button>
      </div>
    </div>

    <EmptyState
      v-else
      icon="📋"
      title="Sem inscricoes"
      description="Ainda nao ha inscritos nesse filtro."
    />

    <!-- Detail modal -->
    <div v-if="detail" class="modal-overlay" @click.self="detail = null">
      <div class="modal-card detail-modal">
        <h2 class="modal-title">
          {{ detail.participanteNome }}
          <FaixaChip :tipo="detail.tipoInscricao" />
        </h2>
        <div class="detail-grid">
          <div class="detail-row"><span>Evento</span><strong>{{ detail.evento?.nome }}</strong></div>
          <div class="detail-row"><span>CPF</span><strong>{{ formatCpf(detail.participanteCpf) }}</strong></div>
          <div class="detail-row"><span>Comprador</span><strong>{{ detail.comprador?.nome }}</strong></div>
          <div class="detail-row"><span>Email</span><strong>{{ detail.comprador?.email }}</strong></div>
          <div class="detail-row"><span>WhatsApp</span><strong>{{ detail.comprador?.telefone }}</strong></div>
          <div class="detail-row"><span>Plano</span><strong>{{ detail.plano?.nome }}</strong></div>
          <div class="detail-row"><span>Status</span><StatusChip :status="detail.statusPagamento" /></div>
        </div>

        <!-- Edicao de contato: nasceu de um telefone digitado errado no
             cadastro, que fazia a cobranca por WhatsApp nunca chegar a
             ninguem. Edita o Comprador, nao o participante — corrige o
             contato de todo o grupo de uma vez. -->
        <div v-if="authStore.canEditarContato" class="custom-section">
          <div class="contato-cabecalho">
            <h3 class="sub-title">Contato do comprador</h3>
            <button v-if="!editandoContato" class="btn-secondary btn-sm" @click="abrirEdicaoContato()">
              Editar contato
            </button>
          </div>
          <div v-if="editandoContato" class="form-contato">
            <div class="form-row">
              <label class="label">Nome</label>
              <input class="input" v-model="contatoForm.nome" maxlength="255" />
            </div>
            <div class="form-row">
              <label class="label">Email</label>
              <input class="input" type="email" v-model="contatoForm.email" maxlength="255" />
            </div>
            <div class="form-row">
              <label class="label">WhatsApp</label>
              <input class="input" v-model="contatoForm.telefone" maxlength="20" />
            </div>
            <div v-if="erroContato" class="error-banner">{{ erroContato }}</div>
            <div class="modal-actions">
              <button class="btn-secondary" @click="fecharEdicaoContato()">Cancelar</button>
              <button class="btn-primary" :disabled="salvandoContato" @click="salvarContato()">
                {{ salvandoContato ? 'Salvando...' : 'Salvar contato' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="detail.grupoInscricao && detail.grupoInscricao.tamanho > 1" class="grupo-section">
          <h3 class="sub-title">
            👥 Grupo ({{ detail.grupoInscricao.tamanho }} pessoas)
            <small class="grupo-hint">1 cobrança para todo o grupo</small>
          </h3>
          <div class="grupo-list">
            <div
              v-for="p in detail.grupoInscricao.inscricoes"
              :key="p.id"
              class="grupo-item"
              :class="{ 'grupo-item-self': p.id === detail.id }"
            >
              <div class="grupo-pos">{{ p.posicaoNoGrupo }}</div>
              <div class="grupo-nome">
                <strong>{{ p.participanteNome }}</strong>
                <FaixaChip :tipo="p.tipoInscricao" />
                <small v-if="p.participanteCpf">{{ formatCpf(p.participanteCpf) }}</small>
                <small v-else class="text-muted">sem CPF (convidado)</small>
              </div>
              <StatusChip :status="p.statusPagamento" />
              <span v-if="p.id === detail.id" class="tag-self">esta</span>
            </div>
          </div>
          <div v-if="authStore.canSeeFinanceiro" class="grupo-totals">
            <div class="detail-row"><span>Cobrado (total)</span><strong>{{ formatMoney(detail.grupoInscricao.valorCobrado) }}</strong></div>
            <div class="detail-row"><span>Inscrições ({{ detail.grupoInscricao.tamanho }} × {{ formatMoney(detail.grupoInscricao.valorPlanoTotal / detail.grupoInscricao.tamanho) }})</span><strong>{{ formatMoney(detail.grupoInscricao.valorPlanoTotal) }}</strong></div>
            <div class="detail-row"><span>Taxa de serviço</span><strong>{{ formatMoney((detail.grupoInscricao.comissaoPlataforma || 0) + (detail.grupoInscricao.taxaGateway || 0)) }}</strong></div>
          </div>
        </div>

        <div v-if="detail.cupomCodigo" class="cupom-applied">
          🎟️ Cupom <strong>{{ detail.cupomCodigo }}</strong>
          aplicou desconto de <strong>{{ formatMoney(detail.descontoTotal || 0) }}</strong>
          <span v-if="authStore.canSeeFinanceiroCompleto">
            ({{ formatMoney(detail.descontoPlataforma || 0) }} seu + {{ formatMoney(detail.descontoEvento || 0) }} do evento)
          </span>
        </div>

        <div v-if="detail.participanteCamposCustom && Object.keys(detail.participanteCamposCustom).length" class="custom-section">
          <h3 class="sub-title">Campos custom</h3>
          <div class="detail-grid">
            <div v-for="(v, k) in detail.participanteCamposCustom" :key="k" class="detail-row">
              <span>{{ k }}</span><strong>{{ v }}</strong>
            </div>
          </div>
        </div>

        <div v-if="authStore.canSeeFinanceiro && detail.pagamentos?.length" class="custom-section">
          <h3 class="sub-title">Pagamentos</h3>
          <div class="table-wrap"><table class="pag-table">
            <thead><tr>
              <th>Vencimento</th><th>Bruto</th><th>Líquido</th>
              <th v-if="authStore.canSeeFinanceiroCompleto" class="col-gateway">No gateway</th>
              <th>Status</th><th>Pago em</th>
            </tr></thead>
            <tbody>
              <tr v-for="p in detail.pagamentos" :key="p.id">
                <td>{{ formatDate(p.vencimento) }}</td>
                <td>{{ formatMoney(p.valor) }}</td>
                <td class="liq">{{ p.valorLiquido != null ? formatMoney(p.valorLiquido) : '—' }}</td>
                <!-- O que o gateway pagou de fato. Só ROOT/ADMIN: o organizador
                     combinou a taxa fixa e não precisa saber qual gateway
                     processou; a diferença é da plataforma. -->
                <td v-if="authStore.canSeeFinanceiroCompleto" class="col-gateway">
                  {{ p.valorLiquidoGateway != null ? formatMoney(p.valorLiquidoGateway) : '—' }}
                </td>
                <td><span class="mini-chip" :class="`mc-${p.status.toLowerCase()}`">{{ p.status }}</span></td>
                <td>{{ p.pagoEm ? formatDate(p.pagoEm) : '—' }}</td>
              </tr>
            </tbody>
          </table></div>
        </div>

        <div v-if="authStore.canManageEventos" class="custom-section">
          <h3 class="sub-title">Faixa da inscrição</h3>
          <div class="faixa-opcoes">
            <button
              v-for="f in FAIXAS"
              :key="f.valor"
              class="faixa-opcao"
              :class="{ 'faixa-opcao-ativa': detail.tipoInscricao === f.valor }"
              :disabled="corrigindoFaixa"
              @click="corrigirFaixa(f.valor)"
            >
              <strong>{{ f.rotulo }}</strong>
              <small>{{ f.regra }}</small>
            </button>
          </div>
          <!-- O aviso e a parte que importa: sem ele o admin corrige a faixa e
               vai embora achando que a cobranca acompanhou. -->
          <p class="faixa-aviso">
            Corrigir a faixa acerta o <strong>cadastro</strong>, não a cobrança. As parcelas
            já emitidas continuam como estão — a diferença de valor se resolve com um
            lançamento manual na página de cobranças.
          </p>
          <p v-if="detail.tipoCorrigidoEm" class="faixa-rastro">
            Corrigida por {{ detail.tipoCorrigidoPor?.name || 'admin removido' }}
            em {{ formatDate(detail.tipoCorrigidoEm) }}.
          </p>
        </div>

        <div v-if="authStore.canManageEventos" class="custom-section">
          <h3 class="sub-title">Anotacoes (interno)</h3>
          <textarea class="textarea" rows="3" v-model="anotacaoLocal" @blur="salvarAnotacao" maxlength="2000"></textarea>
        </div>

        <div class="modal-footer">
          <div class="footer-email">
            <select class="reenvio-select" v-model="tipoReenvio" :disabled="reenviando">
              <option
                v-for="t in tiposReenvio"
                :key="t.valor"
                :value="t.valor"
                :disabled="!!motivoDe(t.valor)"
              >{{ t.rotulo }}{{ motivoDe(t.valor) ? ' — indisponível' : '' }}</option>
            </select>
            <button
              class="btn-secondary btn-reenviar"
              :disabled="reenviando || !!motivoDe(tipoReenvio)"
              @click="reenviarEmail"
            >{{ reenviando ? 'Reenviando…' : 'Reenviar e-mail' }}</button>
          </div>
          <p v-if="motivoDe(tipoReenvio)" class="reenvio-aviso">{{ motivoDe(tipoReenvio) }}</p>

          <div class="footer-botoes">
            <button
              v-if="podeCancelar"
              class="btn-danger"
              :disabled="cancelando"
              @click="cancelarInscricao"
            >{{ cancelando ? 'Cancelando…' : 'Cancelar inscrição' }}</button>
            <span class="footer-espaco" aria-hidden="true"></span>
            <button v-if="authStore.canManageEventos" class="btn-secondary" @click="syncInscricao">Sincronizar com {{ nomeDoGateway }}</button>
            <button class="btn-primary" @click="detail = null">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import { useInscricoesStore } from '../../stores/inscricoes'
import { TIPOS_REENVIO, motivoIndisponivel } from '../../lib/reenvioEmail'
import { FAIXAS, rotuloDaFaixa } from '../../lib/faixas'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import StatusChip from '../../components/ui/StatusChip.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import SemEventoBanner from '../../components/ui/SemEventoBanner.vue'
import FaixaChip from '../../components/ui/FaixaChip.vue'

export default {
  name: 'InscricoesPage',
  components: { SkeletonLoader, StatusChip, EmptyState, SemEventoBanner, FaixaChip },
  data() {
    return {
      q: '', eventoSlug: '', status: '',
      detail: null,
      anotacaoLocal: '',
      debounceTimer: null,
      cancelando: false,
      reenviando: false,
      corrigindoFaixa: false,
      exportando: false,
      editandoContato: false,
      contatoForm: { nome: '', email: '', telefone: '' },
      erroContato: '',
      salvandoContato: false,
      FAIXAS,
      tipoReenvio: 'auto',
      tiposReenvio: TIPOS_REENVIO
    }
  },
  computed: {
    // O botao precisa dizer para onde vai perguntar: a inscricao e sincronizada
    // contra o gateway que cobrou, e nem todo plano e do Asaas.
    nomeDoGateway() {
      const g = this.detail?.plano?.gateway || 'ASAAS'
      return g === 'MERCADO_PAGO' ? 'Mercado Pago' : 'Asaas'
    },
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    store() { return useInscricoesStore() },
    organizadorSemEventos() {
      return this.authStore.isOrganizador && !this.eventosStore.loading && this.eventosStore.eventos.length === 0
    },
    podeCancelar() {
      if (!this.detail) return false
      const role = this.authStore.role
      const permitido = role === 'ROOT' || role === 'ADMIN' || role === 'ORGANIZADOR'
      if (!permitido) return false
      const s = this.detail.statusPagamento
      return s !== 'CANCELADA' && s !== 'QUITADA'
    }
  },
  async mounted() {
    // A notificacao push abre /admin/inscricoes?q=<comprador>: o toque leva
    // direto a pessoa que pagou, nao a lista inteira. Lido antes do fetch para
    // a primeira busca ja sair filtrada.
    if (this.$route.query.q) this.q = String(this.$route.query.q)
    await this.eventosStore.fetchAll()
    await this.fetch()
  },
  methods: {
    debouncedFetch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.fetch(1), 350)
    },
    async fetch(page = 1) {
      await this.store.fetch({
        page,
        ...(this.eventoSlug && { eventoSlug: this.eventoSlug }),
        ...(this.status && { status: this.status }),
        ...(this.q && { q: this.q })
      })
    },
    changePage(p) { this.fetch(p) },
    async openDetail(inscricao) {
      try {
        const full = await this.store.getOne(inscricao.id)
        this.detail = full
        this.anotacaoLocal = full.anotacao || ''
        // Sem isso a escolha feita numa inscricao vazaria para a proxima que o
        // admin abrisse — e ele clicaria "reenviar" achando que era automatico.
        this.tipoReenvio = 'auto'
        this.editandoContato = false
      } catch {
        this.$toast.open({ message: 'Falha ao carregar', type: 'error', position: 'top-right' })
      }
    },
    async salvarAnotacao() {
      if (!this.detail || this.anotacaoLocal === (this.detail.anotacao || '')) return
      try {
        await this.store.updateAnotacao(this.detail.id, this.anotacaoLocal)
        this.detail.anotacao = this.anotacaoLocal
        this.$toast.open({ message: 'Anotacao salva', type: 'success', position: 'top-right', duration: 2000 })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    /**
     * Sempre habilitado: a API escolhe entre confirmacao com QR e cobranca com
     * link conforme o status do pagamento, entao nao ha estado em que o botao
     * nao faca nada util.
     */
    motivoDe(tipo) {
      return motivoIndisponivel(tipo, this.detail)
    },
    async reenviarEmail() {
      this.reenviando = true
      try {
        const res = await this.store.reenviarConfirmacao(this.detail.id, this.tipoReenvio)
        this.$toast.open({ message: res?.message || 'E-mail reenviado', type: 'success', position: 'top-right', duration: 6000 })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao reenviar', type: 'error', position: 'top-right' })
      } finally {
        this.reenviando = false
      }
    },
    /**
     * `jspdf`/`jspdf-autotable` so carregam aqui, no clique — nao no topo do
     * arquivo. O painel inteiro ainda divide bundle com as paginas publicas
     * (main.js importa tudo de cara), entao uma lib de PDF so deve pesar para
     * quem de fato exporta, nao para quem abre o formulario de inscricao.
     */
    async exportarListaPdf() {
      if (!this.eventoSlug || this.exportando) return
      this.exportando = true
      try {
        const data = await this.store.exportarLista(this.eventoSlug)
        if (!data.total) {
          this.$toast.open({ message: 'Esse evento nao tem inscritos para exportar.', type: 'info', position: 'top-right' })
          return
        }
        const { gerarPdfListaInscritos, nomeDoArquivo } = await import('../../lib/exportarListaPdf')
        const doc = await gerarPdfListaInscritos(data)
        doc.save(nomeDoArquivo(data.evento))
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao gerar o PDF', type: 'error', position: 'top-right' })
      } finally {
        this.exportando = false
      }
    },
    /**
     * Muda so o cadastro. A confirmacao diz isso em voz alta porque o efeito
     * que o admin espera — a cobranca acertar sozinha — e justamente o que nao
     * acontece: parcela ja paga viraria estorno parcial no gateway.
     */
    async corrigirFaixa(tipo) {
      if (!this.detail || this.detail.tipoInscricao === tipo) return
      const msg = `Mudar a faixa de "${this.detail.participanteNome}" para ${rotuloDaFaixa(tipo)}?\n\n`
        + 'A cobrança já emitida NÃO muda. Se o valor ficou diferente, acerte por '
        + 'lançamento manual na página de cobranças.'
      if (!confirm(msg)) return

      this.corrigindoFaixa = true
      try {
        const res = await this.store.corrigirTipo(this.detail.id, tipo)
        this.detail.tipoInscricao = res.inscricao.tipoInscricao
        this.detail.tipoCorrigidoEm = res.inscricao.tipoCorrigidoEm
        this.detail.tipoCorrigidoPor = { name: this.authStore.admin?.name }
        // A lista do grupo mostra a mesma faixa num outro chip: sem isso os
        // dois discordariam ate o admin reabrir o modal.
        const noGrupo = this.detail.grupoInscricao?.inscricoes?.find(i => i.id === this.detail.id)
        if (noGrupo) noGrupo.tipoInscricao = res.inscricao.tipoInscricao
        this.$toast.open({ message: res.message, type: 'success', position: 'top-right', duration: 8000 })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao corrigir a faixa', type: 'error', position: 'top-right' })
      } finally {
        this.corrigindoFaixa = false
      }
    },
    abrirEdicaoContato() {
      this.contatoForm = {
        nome: this.detail.comprador?.nome || '',
        email: this.detail.comprador?.email || '',
        telefone: this.detail.comprador?.telefone || ''
      }
      this.erroContato = ''
      this.editandoContato = true
    },
    fecharEdicaoContato() {
      this.editandoContato = false
      this.erroContato = ''
    },
    async salvarContato() {
      this.erroContato = ''
      this.salvandoContato = true
      try {
        const res = await this.store.atualizarContato(this.detail.id, { ...this.contatoForm })
        this.detail.comprador = { ...this.detail.comprador, ...res.comprador }
        this.editandoContato = false
        this.$toast.open({ message: res.message, type: 'success', position: 'top-right' })
      } catch (err) {
        this.erroContato = err.response?.data?.message || 'Falha ao salvar o contato.'
      } finally {
        this.salvandoContato = false
      }
    },
    async syncInscricao() {
      if (!this.detail) return
      try {
        const res = await this.store.sync(this.detail.id)
        this.detail.statusPagamento = res.statusPagamento
        this.$toast.open({ message: `Sincronizado com ${this.nomeDoGateway}`, type: 'success', position: 'top-right' })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    async cancelarInscricao() {
      if (!this.detail) return
      const nome = this.detail.participanteNome
      const grupo = this.detail.grupoInscricao
      const emGrupo = grupo && grupo.tamanho > 1
      const msg = emGrupo
        ? `Essa inscrição faz parte de um GRUPO de ${grupo.tamanho} pessoas com uma única cobrança.\n\nCancelar aqui vai cancelar TODAS as ${grupo.tamanho} inscrições do grupo (${grupo.inscricoes.map(p => p.participanteNome).join(', ')}) e o pagamento no Asaas.\n\nContinuar?`
        : `Cancelar a inscrição de "${nome}"?\n\nIsso cancela a cobrança no Asaas e marca a inscrição como CANCELADA. Ação irreversível.`
      if (!confirm(msg)) return
      this.cancelando = true
      try {
        const res = await this.store.cancelar(this.detail.id)
        this.detail.statusPagamento = 'CANCELADA'
        this.$toast.open({ message: res?.message || 'Inscrição cancelada', type: 'success', position: 'top-right' })
        if (res?.avisos?.asaas) {
          this.$toast.open({ message: res.avisos.asaas, type: 'warning', position: 'top-right', duration: 8000 })
        }
        await this.fetch(this.store.meta?.page || 1)
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao cancelar', type: 'error', position: 'top-right' })
      } finally {
        this.cancelando = false
      }
    },
    formatDate(d) {
      try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
    },
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    formatCpf(cpf) {
      const c = String(cpf || '').replace(/\D/g, '')
      if (c.length !== 11) return cpf
      return `${c.slice(0,3)}.${c.slice(3,6)}.${c.slice(6,9)}-${c.slice(9)}`
    }
  }
}
</script>

<style scoped>
/*
 * O card e item flex do `.modal-overlay`, e item flex nasce com
 * `min-width: auto` — ou seja, se recusa a encolher abaixo do proprio
 * min-content. Num modal com botao de texto longo isso faz o card ficar mais
 * largo que a tela do celular e vazar para a direita.
 *
 * O `scoped` do Vue limita isso ao modal desta pagina; a `.modal-card` global
 * continua intacta para o ConfirmDialog e o EventoForm.
 */
.modal-card { min-width: 0; }

/*
 * Footer proprio em vez da `.modal-actions` global.
 *
 * Aquela e um flex de uma linha so, com `justify-content: flex-end` e sem
 * wrap: com o dropdown a mais os itens estouravam, e como o alinhamento e a
 * direita o excesso vazava pela ESQUERDA, para fora do modal. Nao da pra
 * consertar la porque o ConfirmDialog e o EventoForm usam a mesma classe.
 *
 * Aqui o footer e uma coluna de faixas: reenvio, aviso, botoes.
 */
.modal-footer {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-base);
  border-top: 1px solid var(--color-bg-separator);
}

/* O reenvio e um controle composto (escolha + acao), nao um botao solto:
   ganha faixa propria em vez de disputar espaco com Fechar e Cancelar. */
.footer-email { display: flex; gap: var(--spacing-sm); align-items: stretch; min-width: 0; }
.reenvio-select {
  flex: 1 1 auto;
  /* `min-width: 0` sozinho nao basta: num flex de coluna ele governa o eixo
     principal (altura). O <select> nativo se dimensiona pela opcao mais longa,
     e sem `max-width` ele estoura o modal para a direita no celular. */
  min-width: 0;
  width: 100%;
  max-width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-sm);
  background: #fff;
  font-size: var(--text-sm);
  color: var(--color-text-primary);
}
.reenvio-select:disabled { opacity: 0.6; }
.btn-reenviar { white-space: nowrap; flex: 0 0 auto; }

.reenvio-aviso {
  margin: 0;
  overflow-wrap: anywhere;
  font-size: var(--text-sm);
  line-height: 1.5;
  color: var(--color-text-muted);
}

.footer-botoes { display: flex; gap: var(--spacing-sm); align-items: center; flex-wrap: wrap; min-width: 0; }
/* Empurra Sincronizar e Fechar para a direita, deixando o destrutivo isolado
   na esquerda em vez de encostado nas acoes de uso comum. */
.footer-espaco { flex: 1 1 auto; }
.footer-botoes button { white-space: nowrap; }

@media (max-width: 540px) {
  .footer-email { flex-direction: column; }
  .btn-reenviar { width: 100%; }
  /* `column-reverse` sobre a ordem do DOM poe Fechar em cima e
     "Cancelar inscricao" por ultimo — longe do polegar em repouso. */
  .footer-botoes { flex-direction: column-reverse; align-items: stretch; }
  .footer-espaco { display: none; }
}
.filters {
  display: flex; gap: var(--spacing-sm);
  padding: var(--spacing-base);
  margin-bottom: var(--spacing-base);
  flex-wrap: wrap;
}
.search { flex: 1 1 200px; min-width: 0; }
.select { flex: 1 1 140px; max-width: 200px; min-width: 0; }
.btn-exportar { flex: 0 0 auto; white-space: nowrap; }
@media (max-width: 540px) {
  .filters { padding: 12px; }
  .search, .select { max-width: 100%; flex-basis: 100%; }
  .btn-exportar { width: 100%; }
}
.table-card { padding: 0; overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 10px var(--spacing-base);
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 12px var(--spacing-base);
  font-size: var(--text-sm);
  border-bottom: 1px solid var(--color-bg-separator);
}
.table tr.clickable { cursor: pointer; }
.table tr:hover td { background: var(--color-bg-hover); }
small { color: var(--color-text-muted); font-size: var(--text-xs); }
.text-muted { color: var(--color-text-muted); }
.pagination {
  display: flex; align-items: center; justify-content: center; gap: var(--spacing-base);
  padding: var(--spacing-base);
  border-top: 1px solid var(--color-bg-separator);
}
.page-info { font-size: var(--text-sm); color: var(--color-text-muted); }

.detail-modal { max-width: 640px; }
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-sm) var(--spacing-md); }
.detail-row { display: flex; flex-direction: column; gap: 2px; font-size: var(--text-sm); }
.detail-row span { color: var(--color-text-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
.custom-section { margin-top: var(--spacing-lg); }

/* Tres alvos lado a lado, nao um <select>: a faixa atual precisa ser visivel
   sem abrir nada, porque e ela que a portaria acabou de contestar. */
.faixa-opcoes { display: flex; gap: var(--spacing-sm); flex-wrap: wrap; }
.faixa-opcao {
  flex: 1 1 120px; min-width: 0;
  display: flex; flex-direction: column; gap: 2px;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  background: #fff;
  text-align: left; cursor: pointer;
}
.faixa-opcao strong { font-size: var(--text-sm); }
.faixa-opcao small { font-size: var(--text-xs); color: var(--color-text-muted); }
.faixa-opcao:disabled { opacity: 0.6; cursor: default; }
.faixa-opcao-ativa {
  border-color: var(--color-accent);
  background: var(--color-bg-hover);
  cursor: default;
}
.faixa-aviso {
  margin: var(--spacing-sm) 0 0;
  font-size: var(--text-sm); line-height: 1.5;
  color: var(--color-text-muted);
}
.faixa-rastro {
  margin: var(--spacing-xs) 0 0;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}
@media (max-width: 540px) {
  /* Com 3 alvos de 120px o wrap deixa um sozinho na segunda linha; em coluna a
     lista fica previsivel e cada alvo ganha altura de toque. */
  .faixa-opcoes { flex-direction: column; }
}
.sub-title { font-size: var(--text-md); margin-bottom: var(--spacing-sm); }
.btn-sm { padding: 6px 12px; font-size: var(--text-xs); }
.contato-cabecalho { display: flex; align-items: baseline; justify-content: space-between; gap: var(--spacing-sm); }
.form-contato { display: flex; flex-direction: column; gap: var(--spacing-sm); margin-top: var(--spacing-sm); }
.pag-table { width: 100%; font-size: var(--text-xs); }
.liq { color: var(--color-success); font-weight: 600; }
/* Informacao de conciliacao, nao o numero que se le na tabela: discreta pra
   nao competir com o liquido combinado, que e o que vale pra igreja. */
.col-gateway { color: var(--color-text-muted); font-size: var(--text-xs); font-weight: 400; }
.cupom-applied {
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: #fef3c7; color: #92400e;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
}
.pag-table th, .pag-table td { padding: 6px 4px; text-align: left; border-bottom: 1px solid var(--color-bg-separator); }
.mini-chip {
  display: inline-block;
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  padding: 1px 4px; border-radius: 3px;
}
.mc-pending { background: #fef3c7; color: #92400e; }
.mc-received { background: #dcfce7; color: #166534; }
.mc-overdue { background: #fee2e2; color: #b91c1c; }
.mc-refunded { background: #f3f4f6; color: #4b5563; }
.mc-deleted { background: #f3f4f6; color: #4b5563; }
.grupo-badge {
  display: inline-block; margin-left: 6px;
  background: rgba(37,99,235,0.1); color: var(--color-accent);
  font-size: 11px; font-weight: 700;
  padding: 2px 6px; border-radius: 10px;
  vertical-align: middle;
}
.grupo-section { margin-top: var(--spacing-lg); }
.grupo-section .sub-title { display: flex; align-items: baseline; gap: var(--spacing-sm); flex-wrap: wrap; }
.grupo-hint { font-size: var(--text-xs); color: var(--color-text-muted); font-weight: 400; text-transform: none; letter-spacing: 0; }
.grupo-list { display: flex; flex-direction: column; gap: var(--spacing-xs); }
.grupo-item {
  display: flex; align-items: center; gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
}
.grupo-item-self { background: var(--color-bg-hover); border-color: var(--color-accent); }
.grupo-pos {
  width: 28px; height: 28px; border-radius: 999px;
  background: var(--color-bg-hover); color: var(--color-text-secondary);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.grupo-nome { flex: 1; display: flex; flex-direction: column; }
.grupo-nome small { font-size: 11px; color: var(--color-text-muted); }
.tag-self {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  padding: 2px 6px; border-radius: 3px;
  background: var(--color-accent); color: #fff;
}
.grupo-totals {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-sm);
  border-top: 1px dashed var(--color-bg-separator);
  display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-xs) var(--spacing-md);
}
@media (max-width: 540px) {
  .grupo-totals { grid-template-columns: 1fr; }
}
@media (max-width: 540px) {
  .detail-grid { grid-template-columns: 1fr; }
}
</style>
